import BaseSliderElement from '@webtides/slider-element';
import { defineElement, html } from '@webtides/element-js';
import SliderElementEvents from '@webtides/slider-element/src/SliderElementEvents';

class SliderElement extends BaseSliderElement {
    itemsCount = 0;
    #scrollTimer = null;
    items = null;
    #scrollToIndex = false;

    connected() {
        super.connected();
        this.requestUpdate().then(() => {
            if (this.groupSlides) this.calcGroupCount();
            this.setAttribute('cloak', 'false');
        });
    }

    properties() {
        return {
            ...super.properties(),
            groupSlides: false,
            selectedGroupIndicatorIndex: 0,
            groupsCount: 0,
        };
    }

    get canSlideLeft() {
        return this.groupSlides ? this.selectedGroupIndicatorIndex > 0 : this.selectedIndex > 0;
    }

    get canSlideRight() {
        return this.groupSlides
            ? this.selectedGroupIndicatorIndex < this.groupsCount - 1
            : this.selectedIndex < this.itemsCount;
    }

    events() {
        return {
            ...super.events(),
            '.dot': {
                click: e => {
                    const indicator = e.target.closest('.dot');
                    if (this.groupSlides) {
                        const selectedIndicator = Array.from(indicator.parentNode.children).indexOf(indicator);
                        this.selectedIndex = this.getIndexOfNextGroupElement(selectedIndicator);
                    } else {
                        this.selectedIndex = Array.from(indicator.parentNode.children).indexOf(indicator);
                    }

                    this.scrollToIndex();
                },
            },
        };
    }

    watch() {
        return {
            selectedIndex: () => {
                this.dispatch(SliderElementEvents.SLIDER_RUN, this.selectedIndex);
                if (this.groupSlides) {
                    const selectedItem = this.items[this.selectedIndex];
                    this.selectedGroupIndicatorIndex = Math.floor(selectedItem?.offsetLeft / this.offsetWidth);
                }
            },
        };
    }

    getIndexOfNextGroupElement(newIndex) {
        const groupOffset = newIndex * this.offsetWidth;
        return this.items.findIndex(item => item.offsetLeft >= groupOffset);
    }

    next() {
        if (this.groupSlides) {
            const newIndex = this.selectedGroupIndicatorIndex + 1;
            this.selectedIndex =
                this.rewind && newIndex > this.groupsCount
                    ? 0
                    : Math.min(this.itemsCount, this.getIndexOfNextGroupElement(newIndex));
        } else {
            const newIndex = this.selectedIndex + this.itemsToScroll;
            this.selectedIndex = this.rewind && newIndex > this.itemsCount ? 0 : Math.min(this.itemsCount, newIndex);
        }

        if (this.autoSelect && this.canSlide) {
            //check if element is already visible
            const target = this.items[this.selectedIndex];
            const parent = this.$refs.scroller;
            const { marginLeft, marginRight } = getComputedStyle(target);
            const targetWidth = target.offsetWidth + parseInt(marginLeft) + parseInt(marginRight);
            if (target.offsetLeft + targetWidth < parent.scrollLeft + parent.offsetWidth) {
                // means is in bounds
                this.next();
                return;
            }
        }
        this.scrollToIndex();
    }

    previous() {
        if (this.groupSlides) {
            const newIndex = this.selectedGroupIndicatorIndex - 1;
            this.selectedIndex =
                this.rewind && newIndex < 0 ? this.itemsCount : Math.max(this.getIndexOfNextGroupElement(newIndex), 0);
        } else {
            const newIndex = this.selectedIndex - this.itemsToScroll;
            this.selectedIndex = this.rewind && newIndex < 0 ? this.itemsCount : Math.max(newIndex, 0);
        }

        if (this.autoSelect && this.canSlide) {
            //check if element is already visible
            const target = this.items[this.selectedIndex];
            const { marginLeft } = getComputedStyle(target);
            const parent = this.$refs.scroller;
            if (target.offsetLeft - parseInt(marginLeft) > parent.scrollLeft) {
                // means is still in viewport
                this.previous();
                return;
            }
        }

        this.scrollToIndex();
    }

    addResizeListener() {
        try {
            const resizeObserver = new ResizeObserver(() => {
                /*
                 * https://web.dev/snap-after-layout/
                 * this codes attempts to fix scrollsnap Position after layout changes
                 * */
                this.#scrollToIndex = true;
                this.dispatch(SliderElementEvents.SLIDER_RESIZE, this.selectedIndex);
                this.scrollToIndex(false);
                this.startScrollEndTimer();

                if (this.groupSlides) {
                    this.calcGroupCount();
                }
            });
            resizeObserver.observe(this);
        } catch (e) {
            /* no ResizeOberserver no fix */
        }
    }

    /**
     * Calculates the number of groups for a slider base on the total scroll width
     * of the content and the visible width of the slider.
     *
     * This function determines the number of  groups needed to divide the items in the slider, considering the total number
     * of items and the visible width of the slider. It ensures that aöö items fit within the calculated groups.
     *
     * Calculation steps:
     * 1. Retrieves the total scroll width of the content.
     * 2. Retrieves the visible width of the slider.
     * 3. Determines the total number of items in the slider.
     * 4. Calculates the number of items that fit in on group based on the visible width.
     * 5. Calculates the total number of groups needed to accommodate all items.
     *
     * @function calcGroupCount
     * @returns {void}
     * */
    calcGroupCount() {
        const totalScrollWidth = this.$refs.scroller?.scrollWidth ?? 0;
        const visibleWidth = this.offsetWidth;

        const totalItems = this.items.length;

        const itemsPerGroup = Math.ceil(visibleWidth / (totalScrollWidth / totalItems));
        this.groupsCount = Math.ceil(totalItems / itemsPerGroup);
    }

    dotsGroupTemplate() {
        if (!this.$refs?.scroller || this.groupsCount === 1) return;
        let reduceDotsByGroup = [];
        for (let i = 0; i < this.groupsCount; i++) {
            reduceDotsByGroup.push(html`
                <button
                    ref="dots[]"
                    part="dot ${this.selectedGroupIndicatorIndex === i ? 'selected-dot' : ''}"
                    class="dot"
                    aria-pressed="${this.selectedGroupIndicatorIndex === i ? 'true' : 'false'}"
                ></button>
            `);
        }
        return reduceDotsByGroup;
    }

    dotsTemplate() {
        return this.groupSlides
            ? this.dotsGroupTemplate()
            : this.items.map((item, index) => {
                  return html`
                      <button
                          part="dot ${this.selectedIndex === index ? 'selected-dot' : ''}"
                          class="dot"
                          aria-pressed="${this.selectedIndex === index ? 'true' : 'false'}"
                      ></button>
                  `;
              });
    }

    renderControls() {
        return html` ${this.dots ? this.renderDots() : ''} ${this.arrows ? this.renderArrows() : ''} `;
    }

    renderDots() {
        return html` <div part="controls dots">${this.dotsTemplate()}</div>`;
    }

    renderArrows() {
        return html` <div part="controls arrows">${this.arrowsTemplate()}</div>`;
    }

    template() {
        return html`
            <div ref="scroller" part="scroller" class="scroller">
                <slot></slot>
            </div>

            ${this.canSlide ? this.renderControls() : ''}
        `;
    }
}

defineElement('slider-element', SliderElement);
